import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  computeDots,
  computeGridDimensions,
  computeStratifiedGrid,
  MAX_POPULATION,
  MissingDataMode,
  PopulationDot,
  PopulationWidgetParams,
  StratifiedGridInfo,
} from './population.model';

@Component({
  selector: 'dojo-population-widget',
  standalone: true,
  template: `
    <div class="flex h-full flex-col items-center justify-center p-1.5">
      <!-- Dot grid -->
      <div class="flex min-h-0 items-center justify-center self-stretch">
        <div
          class="bg-neutral-0 relative aspect-square max-h-full w-full rounded-lg border"
          style="border-color: var(--neutral-200)"
          [style.max-width.px]="gridMaxWidth()"
        >
          <!-- Stratified section labels -->
          @if (dividerData(); as grid) {
            @for (section of grid.sections; track section.label) {
              <div
                class="absolute text-center"
                [style.left.%]="section.xStart"
                [style.width.%]="section.width"
                [style.top.%]="grid.yEnd + 0.5"
              >
                <span class="text-[9px] leading-none text-neutral-400 dark:text-neutral-500"
                  >{{ section.label }} ({{ section.percentage }}%)</span
                >
              </div>
            }
          }

          <svg
            viewBox="0 0 100 100"
            class="h-full w-full text-neutral-300 dark:text-neutral-700"
            preserveAspectRatio="xMidYMid meet"
          >
            <!-- Stratified divider lines -->
            @if (dividerData(); as grid) {
              @for (x of grid.dividerXPositions; track x) {
                <line
                  [attr.x1]="x"
                  [attr.y1]="grid.yBase"
                  [attr.x2]="x"
                  [attr.y2]="grid.yEnd"
                  stroke="currentColor"
                  stroke-width="0.5"
                  stroke-dasharray="1.5 1"
                />
              }
            }
            @for (dot of dots(); track dot.id) {
              @if (dot.isMissing) {
                <!-- Missing dot: hollow circle with X -->
                <circle
                  [attr.cx]="dot.cx"
                  [attr.cy]="dot.cy"
                  [attr.r]="dot.r"
                  fill="none"
                  [attr.stroke]="dot.color"
                  stroke-width="0.3"
                  stroke-dasharray="0.8 0.4"
                  [style.opacity]="dot.opacity"
                  style="transition: all 0.5s ease-in-out"
                />
                <line
                  [attr.x1]="dot.cx - dot.r * 0.6"
                  [attr.y1]="dot.cy - dot.r * 0.6"
                  [attr.x2]="dot.cx + dot.r * 0.6"
                  [attr.y2]="dot.cy + dot.r * 0.6"
                  [attr.stroke]="dot.color"
                  stroke-width="0.3"
                  [style.opacity]="dot.opacity"
                  style="transition: all 0.5s ease-in-out"
                />
                <line
                  [attr.x1]="dot.cx + dot.r * 0.6"
                  [attr.y1]="dot.cy - dot.r * 0.6"
                  [attr.x2]="dot.cx - dot.r * 0.6"
                  [attr.y2]="dot.cy + dot.r * 0.6"
                  [attr.stroke]="dot.color"
                  stroke-width="0.3"
                  [style.opacity]="dot.opacity"
                  style="transition: all 0.5s ease-in-out"
                />
              } @else {
                <circle
                  [attr.cx]="dot.cx"
                  [attr.cy]="dot.cy"
                  [attr.r]="dot.r"
                  [style.fill]="dot.color"
                  [style.opacity]="dot.opacity"
                  style="transition: all 0.5s ease-in-out"
                />
              }
            }
          </svg>
        </div>
      </div>

      <!-- Legend (hide when stratified divider already shows labels) -->
      @if (!params().showStratifiedDivider) {
        @if (params().demographicCategories; as categories) {
          <div class="mt-1.5 flex flex-wrap items-center justify-center gap-3">
            @for (cat of categories; track cat.label) {
              <span class="flex items-center gap-1">
                <span class="h-2.5 w-2.5 rounded-full" [style.background]="cat.color"></span>
                <span class="text-[9px] text-neutral-600 sm:text-xs dark:text-neutral-400"
                  >{{ cat.label }} ({{ cat.percentage }}%)</span
                >
              </span>
            }
          </div>
        }
      }

      <!-- Population count -->
      @if (params().showPopulationCount !== false) {
        <span class="mt-1.5 text-[10px] text-neutral-400 sm:text-xs dark:text-neutral-500"
          >Population: {{ params().populationSize }}</span
        >
      }

      <!-- Stratified label -->
      @if (params().sampleMethod === 'stratified' && params().demographicCategories?.length) {
        <p class="mt-1 text-center text-xs text-neutral-400 dark:text-neutral-500">
          Sample preserves population proportions
        </p>
      }

      <!-- Quota label -->
      @if (params().sampleMethod === 'quota' && params().demographicCategories?.length) {
        <p class="mt-1 text-center text-xs text-neutral-400 dark:text-neutral-500">Researcher fills each group quota</p>
      }

      <!-- Missing data label -->
      @if (missingLabel()) {
        <span class="mt-1 text-[10px] text-red-400 sm:text-xs">{{ missingLabel() }}</span>
      }

      <!-- Reliability bars -->
      @if (params().reliabilityBars; as bars) {
        <div class="mt-4 w-full max-w-[280px] space-y-2">
          <p class="mb-1 text-center text-[10px] text-neutral-400 dark:text-neutral-500">Sample Averages Comparison</p>
          @for (bar of bars; track bar.label) {
            <div class="flex items-center gap-2">
              <span class="w-24 text-right text-[10px] text-neutral-500 dark:text-neutral-400">{{ bar.label }}</span>
              <div class="h-3 flex-1 overflow-hidden rounded-full bg-neutral-100 dark:bg-neutral-800">
                <div
                  class="h-full rounded-full transition-all duration-1000"
                  [style.width.%]="bar.width"
                  [style.background-color]="bar.color"
                ></div>
              </div>
            </div>
          }
          <p class="mt-1 text-center text-[10px] text-neutral-400 dark:text-neutral-500">
            Random samples ≈ similar &bull; Biased sample = skewed
          </p>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full w-full' },
})
export class PopulationWidgetComponent {
  readonly params = input.required<PopulationWidgetParams>();

  readonly dots = computed<PopulationDot[]>(() => computeDots(this.params()));

  readonly gridMaxWidth = computed<number>(() => {
    const size = Math.max(0, Math.min(MAX_POPULATION, Math.round(this.params().populationSize)));
    const maxSide = Math.max(...Object.values(computeGridDimensions(MAX_POPULATION)));
    const side = Math.max(...Object.values(computeGridDimensions(size)));
    const MIN_WIDTH = 140;
    const MAX_WIDTH = 320;
    return Math.max(MIN_WIDTH, MAX_WIDTH * (side / maxSide));
  });

  readonly dividerData = computed<StratifiedGridInfo | undefined>(() => {
    const p = this.params();
    if (!p.showStratifiedDivider || !p.demographicCategories?.length) {
      return undefined;
    }
    const size = Math.max(0, Math.min(MAX_POPULATION, Math.round(p.populationSize)));
    return computeStratifiedGrid(size, p.demographicCategories);
  });

  readonly missingLabel = computed<string | undefined>(() => {
    const p = this.params();
    if (!p.missingPercentage || p.missingPercentage <= 0) {
      return undefined;
    }
    const count = Math.round((p.populationSize * Math.min(p.missingPercentage, 100)) / 100);
    const modeLabels: Record<MissingDataMode, string> = {
      highlight: `${count} values missing`,
      removed: `${count} cases removed (listwise)`,
      dimmed: `${count} values missing — remaining data still used`,
      imputed: `${count} values estimated (imputed)`,
    };
    return modeLabels[p.missingDataMode ?? 'highlight'];
  });
}
